import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";

const COLS = [
  { key: "planned", label: "Planned" },
  { key: "released", label: "Released" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
];

const SOURCES = [
  "Inhouse",
  "TURBO TECH INDUSTRIES",
  "SV INDIA COATINGS",
  "Delineate Coatings Private Limited",
  "Anu Engineering Industries",
  "Essjay Technologies",
];

function WoCard({ w, item, onMove, onRemove, isDragging = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging: dragging } = useDraggable({ id: w.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-md p-3 hover:border-blue-300 hover:shadow-sm transition cursor-grab active:cursor-grabbing ${
        dragging || isDragging ? "opacity-50 border-blue-400 shadow-lg" : "border-slate-200"
      }`}
      data-testid={`wo-card-${w.code}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 flex-1" {...listeners} {...attributes}>
          <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-1" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{w.code}</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5">{item?.code}</div>
            <div className="text-xs text-slate-500">{item?.name}</div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onPointerDown={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" data-testid={`wo-menu-${w.code}`} className="h-7 w-7">
              <MoreVertical className="w-4 h-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {COLS.filter((c) => c.key !== w.status).map((c) => (
              <DropdownMenuItem key={c.key} onClick={() => onMove(w, c.key)} data-testid={`move-${w.code}-${c.key}`}>
                Move to {c.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => onRemove(w)} className="text-red-600">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">Qty</span>
        <span className="font-semibold text-slate-800 mono">{w.qty}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs gap-2">
        <span className="text-slate-500">Source</span>
        <span className="font-medium text-slate-700 text-right truncate" data-testid={`wo-source-${w.code}`}>{w.source || "Inhouse"}</span>
      </div>
      {w.sales_order_code && (
        <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1 inline-flex items-center gap-1">
          <span className="uppercase tracking-wide text-[9px] font-semibold">From TO</span>
          <span className="mono">{w.sales_order_code}</span>
        </div>
      )}
      {w.notes && <div className="mt-2 text-xs text-slate-500 line-clamp-2">{w.notes}</div>}
    </div>
  );
}

function Column({ col, list, itemMap, onMove, onRemove }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      className={`bg-slate-50/70 border rounded-lg p-3 transition-colors ${
        isOver ? "border-blue-400 bg-blue-50/40 ring-1 ring-blue-200" : "border-slate-200"
      }`}
      data-testid={`column-${col.key}`}
    >
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="text-xs uppercase tracking-[0.14em] font-semibold text-slate-600">{col.label}</div>
        <div className="text-xs text-slate-500">{list.length}</div>
      </div>
      <div className="space-y-2 min-h-[60vh]">
        {list.map((w) => (
          <WoCard key={w.id} w={w} item={itemMap[w.item_id]} onMove={onMove} onRemove={onRemove} />
        ))}
        {list.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-6">Drop work orders here</div>
        )}
      </div>
    </div>
  );
}

export default function WorkOrdersPage() {
  const [wos, setWos] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState({ item_id: "", source: "Inhouse", qty: 100, status: "planned", notes: "" });
  const [busy, setBusy] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    const [w, i] = await Promise.all([api.get("/work-orders"), api.get("/items")]);
    setWos(w.data);
    const fg = i.data.filter((it) => it.type !== "RAW");
    setItems(fg);
    if (!form.item_id && fg.length) setForm((f) => ({ ...f, item_id: fg[0].id }));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));

  const create = async () => {
    if (busy) return;
    if (!form.item_id) return toast.error("Pick an item");
    setBusy(true);
    try {
      await api.post("/work-orders", form);
      toast.success("Work order created");
      setOpen(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (wo, status) => {
    // optimistic update
    setWos((curr) => curr.map((w) => (w.id === wo.id ? { ...w, status } : w)));
    try {
      await api.patch(`/work-orders/${wo.id}`, { status });
      toast.success(`${wo.code} → ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error("Update failed");
      load();
    }
  };

  const remove = async (wo) => {
    if (!window.confirm(`Delete ${wo.code}?`)) return;
    await api.delete(`/work-orders/${wo.id}`);
    load();
  };

  const onDragStart = (e) => setActiveId(e.active.id);
  const onDragEnd = (e) => {
    setActiveId(null);
    const overId = e.over?.id;
    if (!overId) return;
    const wo = wos.find((w) => w.id === e.active.id);
    if (!wo) return;
    if (wo.status === overId) return;
    changeStatus(wo, overId);
  };
  const onDragCancel = () => setActiveId(null);

  const activeWo = wos.find((w) => w.id === activeId);

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Shop Floor</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Work Orders
          </h1>
          <p className="text-sm text-slate-500 mt-1">Drag a card between columns to update its status — or use the menu.</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-wo-btn"><Plus className="w-4 h-4 mr-1.5" />New Work Order</Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md">
            <SheetHeader><SheetTitle>New Work Order</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Item</Label>
                <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                  <SelectTrigger data-testid="wo-item-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="wo-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: parseFloat(e.target.value || 0) })} data-testid="wo-qty-input" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button onClick={create} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="create-wo-btn">Create</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLS.map((col) => (
            <Column
              key={col.key}
              col={col}
              list={wos.filter((w) => w.status === col.key)}
              itemMap={itemMap}
              onMove={changeStatus}
              onRemove={remove}
            />
          ))}
        </div>
        <DragOverlay>
          {activeWo ? <WoCard w={activeWo} item={itemMap[activeWo.item_id]} onMove={() => {}} onRemove={() => {}} isDragging /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
